import sample from './laSpeziaSample.json';
import { maneuverAlongRoads } from '../model/geometry';

const METERS_PER_LATITUDE = 111320;

function destination(center, bearing, meters) {
  const radians = (Number(bearing) * Math.PI) / 180;
  const latitude = Number(center.lat);
  return {
    lat: latitude + (Math.cos(radians) * meters) / METERS_PER_LATITUDE,
    lng: Number(center.lng)
      + (Math.sin(radians) * meters)
        / (METERS_PER_LATITUDE * Math.cos((latitude * Math.PI) / 180)),
  };
}

function distanceMeters(a, b) {
  const x = (Number(b.lng) - Number(a.lng))
    * METERS_PER_LATITUDE * Math.cos((Number(a.lat) * Math.PI) / 180);
  const y = (Number(b.lat) - Number(a.lat)) * METERS_PER_LATITUDE;
  return Math.hypot(x, y);
}

function bearingGap(a, b) {
  const value = Math.abs((((Number(a) - Number(b)) % 360) + 360) % 360);
  return value > 180 ? 360 - value : value;
}

/**
 * Geometric class from the arm layout: a T has two roughly opposite arms, a
 * cross has four roughly orthogonal arms, everything else (Y, skewed, 5+
 * arms) is "altra geometria".
 */
export function classifyGeometry(intersection) {
  if (intersection.type === 'rotatoria') return 'rotatoria';
  const bearings = (intersection.arms || []).map((arm) => Number(arm.bearing));
  if (bearings.length === 3) {
    const opposite = bearings.some((left, index) => (
      bearings.slice(index + 1).some((right) => bearingGap(left, right) >= 150)
    ));
    return opposite ? 'T' : 'altro';
  }
  if (bearings.length === 4) {
    const sorted = [...bearings].sort((left, right) => left - right);
    const orthogonal = sorted.every((value, index) => {
      const gap = (sorted[(index + 1) % 4] - value + 360) % 360;
      return gap >= 55 && gap <= 125;
    });
    return orthogonal ? 'cross' : 'altro';
  }
  return 'altro';
}

/** How many demo signals are added per geometric class, next to the real ones. */
const FIXTURE_SIGNAL_TARGETS = { T: 8, cross: 6, altro: 6, rotatoria: 1 };
const FIXTURE_SIGNAL_SPACING_M = 140;
const FIXTURE_SIGNAL_ID_BASE = 9000;
const FIXTURE_CROSSING_ID_BASE = 9500;

/**
 * The sample network only contains five signalized nodes (four T, one cross),
 * so every legend category except those two would stay empty. Deterministically
 * promote nearby, well-spaced nodes of each geometric class to demo signals.
 */
function extendFixtureSignals(raw) {
  const intersections = raw.intersections || [];
  const signals = [...(raw.signals || [])];
  const crossings = [...(raw.crossings || [])];
  const signalized = new Set(signals.map((signal) => String(signal.intersectionId)));
  const anchors = intersections.filter((item) => signalized.has(String(item.id)));
  const center = anchors.length
    ? {
      lat: anchors.reduce((sum, item) => sum + Number(item.lat), 0) / anchors.length,
      lng: anchors.reduce((sum, item) => sum + Number(item.lng), 0) / anchors.length,
    }
    : intersections[0];
  if (!center) return { signals, crossings, promoted: new Set() };

  const taken = [...anchors];
  const counts = {};
  const promoted = new Set();
  const candidates = intersections
    .filter((item) => !signalized.has(String(item.id)) && (item.arms || []).length >= 3)
    .map((item) => ({ item, geometry: classifyGeometry(item), distance: distanceMeters(center, item) }))
    .sort((left, right) => left.distance - right.distance);

  candidates.forEach(({ item, geometry }) => {
    if ((counts[geometry] || 0) >= (FIXTURE_SIGNAL_TARGETS[geometry] || 0)) return;
    if (taken.some((other) => distanceMeters(other, item) < FIXTURE_SIGNAL_SPACING_M)) return;
    counts[geometry] = (counts[geometry] || 0) + 1;
    taken.push(item);
    promoted.add(String(item.id));

    const n = promoted.size;
    const cycle = [70, 80, 90][n % 3];
    const green = 26 + (n % 5) * 4;
    const yellow = 3;
    const capacity = 1500 + (n % 4) * 300;
    const signalId = FIXTURE_SIGNAL_ID_BASE + n;
    signals.push({
      id: signalId,
      name: item.name,
      lat: item.lat,
      lng: item.lng,
      green,
      yellow,
      red: cycle - green - yellow,
      intersectionId: item.id,
      baseFlow: Math.round(capacity * (0.45 + (n % 4) * 0.1)),
      capacity,
      fixture: true,
    });

    if (n % 2 === 1) {
      const arm = item.arms[0];
      const point = destination(item, Number(arm.bearing), 14);
      const minGreen = 16 + (n % 3) * 4;
      crossings.push({
        id: FIXTURE_CROSSING_ID_BASE + n,
        name: arm.road || item.name,
        lat: Number(point.lat.toFixed(6)),
        lng: Number(point.lng.toFixed(6)),
        length: Number((6.5 + arm.lanes * 3.1).toFixed(1)),
        risk: ['basso', 'medio', 'alto'][n % 3],
        pedH: 120 + (n % 5) * 60,
        minGreen,
        currGreen: minGreen - 4 + (n % 3) * 4,
        signalId,
        fixture: true,
      });
    }
  });

  return { signals, crossings, promoted };
}

let sampleExtension = null;

function sampleFixtureExtension() {
  if (!sampleExtension) sampleExtension = extendFixtureSignals(sample);
  return sampleExtension;
}

function fixtureIntersection(intersectionId) {
  return sample.intersections.find((item) => String(item.id) === String(intersectionId));
}

function fixtureSignal(intersectionId) {
  return sampleFixtureExtension().signals.find((item) => String(item.intersectionId) === String(intersectionId));
}

const MOVEMENT_KINDS = ['straight', 'right', 'left'];

/**
 * Realistic, deterministic manoeuvres used only until the satellite/editor
 * endpoints return data. Their shape is the same shape consumed by the UI and
 * by the production API adapter, so replacing them does not change components.
 */
export function buildFixtureManeuvers(intersectionId) {
  const intersection = fixtureIntersection(intersectionId);
  if (!intersection) return [];
  return (intersection.arms || []).map((arm, index) => {
    const bearing = Number(arm.bearing || index * (360 / Math.max(intersection.arms.length, 1)));
    const kind = MOVEMENT_KINDS[index % MOVEMENT_KINDS.length];
    const exitHeading = (bearing + 180 + (kind === 'right' ? 90 : kind === 'left' ? -90 : 0) + 360) % 360;
    const exitArm = (intersection.arms || []).reduce((best, candidate) => {
      if (candidate === arm) return best;
      const gap = Math.abs((((candidate.bearing - exitHeading) % 360) + 540) % 360 - 180);
      return !best || gap < best.gap ? { arm: candidate, gap } : best;
    }, null)?.arm || arm;
    const fromRoad = sample.roads.find((road) => String(road.id) === String(arm.roadId));
    const toRoad = sample.roads.find((road) => String(road.id) === String(exitArm.roadId));
    const alongRoads = maneuverAlongRoads({
      intersection,
      fromRoad,
      toRoad,
      fromBearing: bearing,
      toBearing: Number(exitArm.bearing),
    });
    const from = destination(intersection, bearing + 180, 42);
    const center = destination(intersection, bearing + (kind === 'straight' ? 180 : kind === 'left' ? 150 : 210), 6);
    const to = destination(intersection, Number(exitArm.bearing), 38);
    const fromRoadName = arm.road || `Approccio ${index + 1}`;
    const toRoadName = exitArm.road || fromRoadName;
    const lanes = Math.max(1, Number(arm.lanes || 1));
    const capacity = Math.max(600, Number(arm.cap || 1200));
    const flow = Math.max(0, Number(arm.flow || Math.round(capacity * 0.55)));
    return {
      id: `fixture-${intersection.id}-${index + 1}`,
      workflow_id: `fixture-${intersection.id}`,
      title: `${fromRoadName} → ${toRoadName}`,
      fromRoad: fromRoadName,
      toRoad: toRoadName,
      inputType: 'veicolare',
      movementType: kind,
      approachBearing: bearing,
      color: ['#2F7ED8', '#D4380D', '#7F5AF0', '#E0A800'][index % 4],
      lanes,
      laneWidth: Number((3.1 + (index % 3) * 0.15).toFixed(2)),
      capSatResult: capacity,
      verdeMin: 0,
      flussi: [{ id: 1, value: String(flow) }],
      latlngs: alongRoads.length > 2 ? alongRoads : [from, center, to],
      isManual: false,
      manual: false,
      inputSource: 'satellite',
      capacitySource: 'capsat',
    };
  });
}

export function buildFixtureConflicts(intersectionId) {
  const labels = buildFixtureManeuvers(intersectionId)
    .map((item) => `${item.title} - Veicolare`);
  const conflicts = [];
  labels.forEach((left, leftIndex) => {
    labels.slice(leftIndex + 1).forEach((right, offset) => {
      if ((leftIndex + offset) % 2 === 0) conflicts.push([left, right]);
    });
  });
  return conflicts;
}

export function buildFixtureOptimizedPlan(intersectionId) {
  const signal = fixtureSignal(intersectionId);
  if (!signal) return null;
  // Keep a mix of optimized and pending intersections so both documented
  // marker states and the manual-parameter fallback are demonstrable.
  if (Number(signal.id) % 2 === 0) return null;
  const maneuvers = buildFixtureManeuvers(intersectionId);
  const cycle = Number(signal.green) + Number(signal.yellow) + Number(signal.red);
  return {
    cycle,
    yellow: Number(signal.yellow),
    allRed: 2,
    optimizedAt: 'fixture',
    source: 'fixture',
    phases: maneuvers.map((maneuver, index) => {
      const green = Math.max(12, Math.round(Number(signal.green) - index * 2));
      const yellow = Number(signal.yellow);
      return {
        maneuverId: maneuver.id,
        nome: maneuver.title,
        green,
        yellow,
        red: Math.max(0, cycle - green - yellow),
      };
    }),
  };
}

export function enrichCatalogFixture(raw) {
  const { signals, crossings, promoted } = raw === sample
    ? sampleFixtureExtension()
    : extendFixtureSignals(raw);
  const signalIds = new Set(signals.map((signal) => String(signal.intersectionId)));
  let signalIndex = 0;
  return {
    ...raw,
    roads: (raw.roads || []).map((road, index) => ({
      ...road,
      flowSource: index % 5 === 0 ? 'observed' : 'estimated',
      monitored: index % 5 === 0,
    })),
    intersections: (raw.intersections || []).map((intersection) => {
      const signalized = signalIds.has(String(intersection.id));
      // Alternate satellite availability among signals so both marker
      // conventions are always visible.
      const satelliteAvailable = signalized && signalIndex++ % 2 === 0;
      return {
        ...intersection,
        type: promoted.has(String(intersection.id)) ? 'semaforizzata' : intersection.type,
        geometryType: intersection.geometryType || classifyGeometry(intersection),
        satelliteAvailable,
        maneuversPreview: signalized ? buildFixtureManeuvers(intersection.id) : [],
      };
    }),
    signals: signals.map((signal) => ({
      ...signal,
      optimizedPlan: buildFixtureOptimizedPlan(signal.intersectionId),
    })),
    crossings,
  };
}
